/**
 * useTimeOnPage Hook
 * Tracks time spent on a page for engagement metrics
 *
 * @module hooks/useTimeOnPage
 * @description Fires engagement event after user spends 30+ seconds on page
 *
 * @example
 * ```tsx
 * // In a page component
 * useTimeOnPage();
 * ```
 */

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackTimeOnPage } from "@/lib/analytics";

/** Time thresholds to track (in seconds) */
const TIME_THRESHOLDS = [30, 60, 120, 300];

export const useTimeOnPage = (): void => {
  const location = useLocation();
  const startTimeRef = useRef<number>(Date.now());
  const trackedThresholds = useRef<Set<number>>(new Set());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  /** Seconds already banked from previous visible stretches on this route. */
  const activeSecondsRef = useRef<number>(0);

  useEffect(() => {
    // Reset on route change
    startTimeRef.current = Date.now();
    trackedThresholds.current.clear();
    activeSecondsRef.current = 0;

    /**
     * Time the page was actually visible.
     *
     * Wall-clock from mount counted background-tab time as engagement, so a tab left
     * open all day reported 300s of "reading". Only visible stretches accumulate.
     */
    const activeSeconds = (): number => {
      const current =
        document.visibilityState === "visible"
          ? Math.floor((Date.now() - startTimeRef.current) / 1000)
          : 0;
      return activeSecondsRef.current + current;
    };

    const checkTimeSpent = () => {
      const secondsSpent = activeSeconds();

      TIME_THRESHOLDS.forEach((threshold) => {
        if (
          secondsSpent >= threshold &&
          !trackedThresholds.current.has(threshold)
        ) {
          trackedThresholds.current.add(threshold);
          trackTimeOnPage(threshold, location.pathname);
        }
      });
    };

    // Check every 10 seconds
    intervalRef.current = setInterval(checkTimeSpent, 10000);

    // Visibility changes bank time; they no longer emit their own event.
    //
    // Two earlier versions of this were wrong. It first fired on EVERY visibility change,
    // sending an unbounded stream — 3,180 events from 114 users over 90 days, more than
    // page_view. Capping it at one still reported the raw elapsed seconds (e.g. 20s, 35s),
    // which contradicted the threshold-only schema and double-reported alongside the
    // interval: 35s visible produced both time_on_page(30) and time_on_page(35).
    //
    // Now every emitted value is one of the documented thresholds, each at most once per
    // route visit — from the interval, or from the flush below when hiding banks enough
    // time to cross one (skipping that flush would lose the threshold entirely if the
    // visitor never returns). GA4's own engagement-time metric covers raw duration.
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Resume: restart the clock so hidden time is never banked.
        startTimeRef.current = Date.now();
        return;
      }

      // Hidden. Bank the stretch that just ended from RAW elapsed time — by the time this
      // event fires visibilityState is already "hidden", so activeSeconds() would measure
      // the current stretch as zero and silently discard it (20s visible → hide → 20s
      // visible would total 20s, not 40s, and miss the 30s threshold).
      const stretch = Math.floor((Date.now() - startTimeRef.current) / 1000);
      activeSecondsRef.current += stretch;
      // Crossing a threshold while hidden is caught by the next interval tick once the
      // tab is visible again, so nothing needs to be emitted here.
      checkTimeSpent();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [location.pathname]);
};

export default useTimeOnPage;
